import { KendoGrid, KendoGridField, KendoGridOptions, KendoGridRow } from "@Kendo/Components/KendoGrid";
import { GetVhPixels } from "@common/Common";

export type ProjectPropertyData = { [key: string]: string | number | boolean | null };

export class ProjectsGridViewModel
{
    private readonly DEFAULT_COLUMN_WIDTH = "120px";

    grid: KendoGrid | null = null;
    columnsEditorOpened: boolean = false;
    fields: KendoGridField[] = [];
    idFieldId: string;

    showGridColumnsEditor(): void
    {
        this.columnsEditorOpened = true;
    }

    exportGrid()
    {
        if (this.grid)
        {
            this.grid.Export();
        }
    }

    clearFilters()
    {
        if (this.grid)
        {
            this.grid.ClearFilters();
        }
    }

    renderGrid(idFieldId: string, fields: KendoGridField[], defaultFieldIds: string[], options: string | null, changeHandler?: (e: any) => void): void
    {
        this.idFieldId = idFieldId;
        this.fields = fields;

        const columns = this.buildColumns(fields, defaultFieldIds, options);
        const cols: kendo.ui.GridColumn[] = [];
        //cols.push({ selectable: true, width: 40 });

        columns.forEach(c => c.filterable = { multi: true, search: true });
        cols.push(...columns);

        cols[0].locked = true;
        //cols[1].locked = true;

        const gridOptions1: kendo.ui.GridOptions = {
            columns: cols,            
            scrollable: true,
            reorderable: true,
            resizable: true,
            columnMenu: false,
            groupable: true,
            filterable: true,
            selectable: {
                mode: "multiple, row",
                cellAggregates: true,
                checkboxSelection: true,
                ignoreOverlapped: false
            } as any,
            sortable: { allowUnsort: false },
            excel: { fileName: "grid.xlsx", filterable: true },
            change: changeHandler
        };

        const gridOptions: KendoGridOptions = KendoGridOptions.Create(gridOptions1);
        //gridOptions.SetOnGridChange(getSelectedIds);

        //gridOptions.SetColumns(columns);
        //gridOptions.SetGroupable(true);
        gridOptions.SetHeight(GetVhPixels(90));

        //const resetSelectedAlternatives = () => { return; }
        const grid = new KendoGrid(idFieldId, this.fields, null);
        //grid.SetCustomColumnMenu(gridOptions);
        grid.LoadSettings(options);

        this.grid = grid;
        this.grid.Render1("pbGrid", gridOptions);

        //this.attachDropdownEventHandlers();
    }


    private buildColumns(fields: KendoGridField[], defaultFieldIds: string[], options: string | null): kendo.ui.GridColumn[]
    {
        let columns: kendo.ui.GridColumn[] = [];

        const savedColumns = KendoGrid.GetSavedColumns(options);        

        if (savedColumns)
        {
            // Filter saved columns based on available fields
            columns = savedColumns.filter(col => fields.some(f => f.FieldId === col.field));

            // Set default width if not provided
            columns.filter(c => !c.width).forEach(c => c.width = this.DEFAULT_COLUMN_WIDTH);

            // Update template and title for each column based on the matching field
            columns.forEach(col =>
            {
                const matchingField = fields.find(f => f.FieldId === col.field);
                if (matchingField)
                {
                    col.title = matchingField.ColumnTitle;
                    col.template = matchingField.Template;
                    //col.filterable = { multi: true, search: true };
                }
            });
        }
        else
        {
            // Default columns if no saved columns exist
            columns = fields
                .filter(f => defaultFieldIds.includes(f.FieldId))
                .map(f => ({
                    field: f.FieldId,
                    title: f.ColumnTitle,
                    format: f.Format,
                    hidden: f.ColumnHidden,
                    template: f.Template ?? null,
                    width: this.DEFAULT_COLUMN_WIDTH,
                    //filterable: { multi: true, search: true }
                }));
        }

        return columns;
    }


    setProjects(projects: ProjectPropertyData[]): void
    {
        const data = projects.map(a => this.buildGridRow(a));

        this.grid.SetData(data);
        this.grid.RenderProgressBars();
        this.grid.RenderCumulativeColumns();
    } 


    buildGridRow(p: ProjectPropertyData): KendoGridRow
    {
        const row: KendoGridRow = {};

        for (const f of this.fields)
        {
            const value = p[f.FieldId] ?? null;

            // Transform value based on field type
            if (f.Type === "date" && value)
            {
                const d = new Date(value as string);
                // Create a new Date using the UTC components so the local date reflects the UTC day.
                row[f.FieldId] = new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
            } else
            {
                row[f.FieldId] = value;
            }
        }

        return row;
    }


    removeRowById(projectId: string): void
    {
        this.grid.removeDataItem(projectId);
    }

    update(p: ProjectPropertyData): void
    {
        const updated = this.buildGridRow(p);
        this.grid.replaceRow(updated, this.idFieldId);
    }


    getJson(): string
    {
        return this.grid.GetJsonSettings();
    }
}
